import { useQuery } from '@tanstack/react-query';
import { APITypes, LambdaInvocationTypes } from '../../primary-record-types';
import { getIntl } from '../../utils/intl';
import { getMedicalProfileSearchCacheKey } from './cacheKeys';
import urls from '../../utils/urls';
import { appAPI } from '../../utils/api';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('useMedicalProfileSearch', {
    failedToSearch: 'Failed to search',
});

export type UseMedicalProfileSearchArgs = {
    medicalProfileId: string;
    text: string;
};

export type FileArtifactSearchResults =
    | LambdaInvocationTypes.ResolvedReferencedPageFileArtifact
    | LambdaInvocationTypes.ResolvedReferencedAudioFileArtifact
    | APITypes.chatService.GroupedResolvedImageFileArtifacts;

export type UseMedicalProfileSearchReturnValue = {
    searchResults: APITypes.chatService.PerformGeneralMedicalProfileSearchResponse;
    isLoadingSearchResults: boolean;
};

export const isSearchEnabled = (text: string): boolean => !!text && text.length > 2;

export const useMedicalProfileSearch = ({
    medicalProfileId,
    text,
}: UseMedicalProfileSearchArgs): UseMedicalProfileSearchReturnValue => {
    const query = useQuery({
        queryKey: getMedicalProfileSearchCacheKey(medicalProfileId, text),
        queryFn: async () => {
            const request: APITypes.chatService.PerformGeneralMedicalProfileSearchRequest = {
                text,
            };
            return await appAPI.post(urls.api.chats.medicalProfileSearch(medicalProfileId), {
                body: request,
            });
        },
        enabled: isSearchEnabled(text),
    });

    useQueryErrorNotify(query, intl('failedToSearch'));

    return {
        searchResults: query.data || { results: [] },
        isLoadingSearchResults: query.isLoading,
    };
};
